/**
 * What a month filter host adds around Superset's MonthRangePicker so a keyboard and a screen reader
 * can use it, without editing the component (blink-superset-bundle.md, "The month hosts"):
 *
 *   - the trigger is a div with role="button" that answers no key, so Enter and Space open it here;
 *   - it is named by its visible text alone, so it is named here by the host's label and that text
 *     (which keeps what a speech user sees in its name), described by the chosen months in full with
 *     a preset's name, the instructions, hint and error, and marked invalid while the server says so;
 *   - its clear control is a button inside that role, hidden from a screen reader; the host's view
 *     hides it and draws its own (month-filter.blade.php);
 *   - the popup has no role and takes no focus, so it is named as a dialog here and focus moves into
 *     it, onto the chosen month (else this month) — a non-modal dialog, which Tab may leave;
 *   - its month buttons read "Jan" to "Dec" with no year, and mark the choice by colour alone, so
 *     each is named here with its month, year and whether it is chosen, again after a year change;
 *   - the preset the host mounted shows no highlight (the host mounts PHP's months, not the preset
 *     token the component highlights from), so its button is marked aria-pressed here, which the
 *     view also draws;
 *   - Escape is the component's to ignore, and inside an <x-modal> it would close the modal, so while
 *     the popup is open Escape closes the popup and stops at the host;
 *   - after a pick the popup is removed with focus inside it, which falls to <body>, so focus goes
 *     back to the trigger after every close, except a close the reader caused by leaving: Tab out of
 *     the popup, or a press outside the host, which must keep focus where the reader sent it.
 *
 * The selectors are the component's own markup (its role="button" trigger, antd's popover classes, its
 * "Previous year" button and "Jan"-"Dec" labels), which smoke.filters.mjs pins against the built
 * bundle. Re-check this layer in a browser after any Superset upgrade that moves MonthRangePicker.
 */

const TRIGGER = '[role="button"][aria-haspopup="dialog"]';
const POPOVER = '.ant-popover';
const HIDDEN = 'ant-popover-hidden';
const POPOVER_BODY = '.ant-popover-inner';
const PREVIOUS_YEAR = 'button[aria-label="Previous year"]';
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FIRST_MONTH = /(\d{4}-\d{2})/;

const activates = (event) => event.key === 'Enter' || event.key === ' ';

function setAttribute(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}

export function createMonthFilterKeyboard({
    root,
    mount,
    popup,
    ids = {},
    text = {},
    presets = () => ({ literals: {}, labels: {} }),
    chosenMonths = () => null,
    currentPresetKey = () => null,
}) {
    let invalid = false;
    // Set when the popup opens, until focus has been moved into it.
    let wantsFocus = false;
    // Set when the reader leaves the open popup (Tab out, a press elsewhere): its close must not pull
    // focus back. Known before the close is reported, because focus has not landed yet when it is:
    // the browser reports <body> as focused during a focusout.
    let isLeaving = false;

    const trigger = () => mount.querySelector(TRIGGER);

    // A closing popover keeps its classes through antd's leave animation before it is marked hidden;
    // it is already closed, and an Escape pressed meanwhile belongs to whatever is around the filter.
    const openPopover = () => {
        const box = popup.querySelector(POPOVER);
        return box && ! box.classList.contains(HIDDEN) && ! Array.from(box.classList).some((name) => name.endsWith('-leave')) ? box : null;
    };

    const monthButtons = (box) => Array.from(box.querySelectorAll('button'))
        .filter((button) => MONTH_LABELS.includes(button.textContent.trim()))
        .map((button) => ({ button, index: MONTH_LABELS.indexOf(button.textContent.trim()) }));

    /** The year the grid shows: the label between the year buttons. */
    const yearShown = (box) => Number(box.querySelector(PREVIOUS_YEAR)?.nextElementSibling?.textContent?.trim() ?? NaN);

    /**
     * The preset buttons, named by position: the component renders its preset column, in the order the
     * server's `order` lists (MonthPresetDriftTest), before the year buttons. Not by label, which a
     * translated preset name would stop matching.
     */
    function presetButtons(box) {
        const order = presets().order ?? [];
        const buttons = Array.from(box.querySelectorAll('button'));
        const yearButton = box.querySelector(PREVIOUS_YEAR);
        const column = yearButton ? buttons.slice(0, buttons.indexOf(yearButton)) : [];

        return column.length === order.length ? column.map((button, index) => ({ button, key: order[index] })) : [];
    }

    function wire({ invalid: isInvalid = invalid } = {}) {
        invalid = isInvalid;
        const button = trigger();
        if (! button) return;

        // Its own id, so aria-labelledby can name it by its visible text after the label.
        if (ids.trigger && button.id !== ids.trigger) button.id = ids.trigger;
        setAttribute(button, 'aria-labelledby', `${ids.label} ${ids.trigger ?? ids.value}`);
        setAttribute(button, 'aria-describedby', ids.describedBy);
        if (invalid) setAttribute(button, 'aria-invalid', 'true');
        else if (button.getAttribute('aria-invalid') !== null) button.removeAttribute('aria-invalid');
    }

    /** Names the popup and its buttons; run again whenever React re-renders the grid. */
    function decorate(box) {
        const dialog = box.querySelector(POPOVER_BODY) ?? box;
        setAttribute(dialog, 'role', 'dialog');
        setAttribute(dialog, 'aria-labelledby', ids.label);

        const year = yearShown(box);
        const chosen = chosenMonths();

        monthButtons(box).forEach(({ button, index }) => {
            const month = `${year}-${String(index + 1).padStart(2, '0')}`;
            const isChosen = Array.isArray(chosen) && month >= chosen[0] && month <= chosen[1];
            const name = `${text.months?.[index] ?? MONTH_LABELS[index]} ${Number.isNaN(year) ? '' : year}`.trim();
            setAttribute(button, 'aria-label', isChosen ? `${name}, ${text.chosen}` : name);
        });

        presetButtons(box).forEach(({ button, key }) => setAttribute(button, 'aria-pressed', String(key === currentPresetKey())));
    }

    /** Where focus lands in the popup: the chosen month, else this month, else January of the year shown. */
    function focusTarget(box) {
        const year = yearShown(box);
        const months = monthButtons(box);
        const inYear = (month) => month && Number(month.slice(0, 4)) === year ? months.find(({ index }) => index === Number(month.slice(5)) - 1) : null;
        const thisMonth = FIRST_MONTH.exec(presets().literals?.current_month ?? '')?.[1] ?? null;

        return (inYear(chosenMonths()?.[0] ?? null) ?? inYear(thisMonth) ?? months[0] ?? null)?.button ?? null;
    }

    /**
     * Decorates the open popup, and moves focus into it once after it opens. Driven by the popup box's
     * mutations, not a timer or an animation frame: antd renders the popover some time after it
     * reports the opening (a frame Chrome never runs in a background tab), and React re-renders the
     * grid on every pick and year change.
     */
    function refresh() {
        const box = openPopover();
        if (! box) return;

        decorate(box);

        if (wantsFocus) {
            const target = focusTarget(box);
            if (target) {
                wantsFocus = false;
                target.focus();
            }
        }
    }

    // Watching only class changes among the attributes, so the names set above never re-trigger it.
    const observer = new MutationObserver(refresh);
    observer.observe(popup, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });

    function opened() {
        wantsFocus = true;
        isLeaving = false;
        refresh();
    }

    function closed() {
        wantsFocus = false;
        const left = isLeaving;
        isLeaving = false;
        if (left) return;

        // The popup is removed with focus inside it; the reader's place is the trigger. A reader who
        // has moved focus elsewhere (a click on another control) keeps it there.
        const active = document.activeElement;
        if (! active || active === document.body || popup.contains(active)) focusTrigger();
    }

    function focusTrigger() {
        trigger()?.focus();
    }

    /** Clicking the trigger is how the component toggles its popover. */
    function close() {
        if (openPopover()) trigger()?.click();
    }

    function onKeydown(event) {
        const button = trigger();

        if (button && event.target === button && activates(event)) {
            event.preventDefault();
            event.stopPropagation();
            button.click();
            return;
        }

        if (event.key === 'Escape' && openPopover()) {
            // Captured at the host, so it never reaches the picker or a surrounding <x-modal>.
            event.preventDefault();
            event.stopPropagation();
            close();
        }
    }

    function onFocusout(event) {
        const next = event.relatedTarget;
        if (next && ! root.contains(next) && openPopover()) {
            isLeaving = true;
            close();
        }
    }

    /**
     * A press outside the host closes the popup (antd's own rule); the reader's focus goes with it. A
     * press that does not close it (one antd ignores) must not leave the flag for a later close, so it
     * is dropped once that press is done with, if the popup is still open.
     */
    function onPointerdown(event) {
        if (! openPopover() || root.contains(event.target)) return;

        isLeaving = true;
        setTimeout(() => {
            if (openPopover()) isLeaving = false;
        }, 0);
    }

    root.addEventListener('keydown', onKeydown, true);
    root.addEventListener('focusout', onFocusout);
    document.addEventListener('pointerdown', onPointerdown, true);

    return {
        wire,
        opened,
        closed,
        focusTrigger,
        destroy() {
            observer.disconnect();
            root.removeEventListener('keydown', onKeydown, true);
            root.removeEventListener('focusout', onFocusout);
            document.removeEventListener('pointerdown', onPointerdown, true);
        },
    };
}
